import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Input>. label/help/error/
// required are ported faithfully (read from the vendor's compiled bundle,
// not guessed) — these are the props real call sites in this app actually
// use (verified: 21 `required=` sites, several real `error=` sites).
//
// Deliberately NOT ported: the declarative `rules`/`validateOn`/
// `onValidate` validation engine, the `InputHandle` imperative ref
// (`{ validate, element }` instead of a plain DOM ref), and `Input.Skeleton`.
// All three are genuinely unused — zero real call sites anywhere in this
// app (grepped for `rules=`, `validateOn`, `onValidate`, `Input.Skeleton`,
// and any ref passed to `<Input>` — all zero). Reimplementing an unused,
// unverifiable validation engine is speculative complexity with no call
// site to check it against; if a future site needs it, that's the moment
// to add it back; the ref this gets instead is a plain HTMLInputElement,
// which is strictly less surprising for the sites that exist today.
// Phase 3.5: `size` joins the shared 32/40/48 control grid. Named and typed
// identically to Select's own SelectSize so the two fields are drop-in
// interchangeable in a filter bar — the reason this was added is that a
// dense toolbar had no way to stand a text field next to a 32px button.
export type InputSize = "sm" | "md" | "lg";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  size?: InputSize;
  /* Phase 4.3 — hides the label VISUALLY, never from assistive tech.
     `label` stays required, which is the point: the 93 raw <input> elements
     scattered through this app exist largely because a toolbar has no room
     for a label above the field, and the escape hatch people reached for was
     a placeholder-only input with no accessible name at all. This gives them
     the layout they wanted without dropping the name. */
  hideLabel?: boolean;
  /** Decorative icon inside the field's leading edge. */
  leadingIcon?: ReactNode;
  /** Rendered at the trailing edge — a clear button, a unit, a spinner. */
  trailingSlot?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, help, error, required, size = "md", hideLabel, leadingIcon, trailingSlot, className, id: providedId, ...rest },
  ref
) {
  const fallbackId = useId();
  const inputId = providedId ?? fallbackId;
  const helpId = help ? `${inputId}-help` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div
      className={cn(
        "rc-input",
        `rc-input--${size}`,
        error && "rc-input--error",
        leadingIcon && "rc-input--with-leading",
        trailingSlot && "rc-input--with-trailing",
        className
      )}
    >
      <label htmlFor={inputId} className={cn("rc-input__label", hideLabel && "sr-only")}>
        {label}
        {required ? (
          <span className="rc-input__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <span className="rc-input__control">
        {leadingIcon ? (
          <span className="rc-input__leading" aria-hidden="true">
            {leadingIcon}
          </span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="rc-input__field"
          {...rest}
        />
        {trailingSlot ? <span className="rc-input__trailing">{trailingSlot}</span> : null}
      </span>
      {help ? (
        <span id={helpId} className="rc-input__help">
          {help}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="rc-input__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});
